import { Button, message } from 'antd';
import { Fragment, useEffect, useMemo, useState } from 'react';
import {
    ScoreCardEvaluationCheckResponse,
    useSubmitScoreCardEvaluationMutation,
    type ScoreCardEvaluationSubmitBody
} from '../../api/tenders';
import { ScoreCardSliderField } from './ScoreCardSliderField';
import done from '../../assets/Done, Check.svg';
import { ServiceComIcon } from '../../components/Icons/ServiceComIcon';
import { ExplanationBanner, SingleSelectScorecard } from '../SignUp/fields/fieldsRender';
import { QuestionAnswer } from '../SignUp';
import arrowIcon from '../../assets/arrow-right-white.svg';
import arrowLeftIcon from '../../assets/arrow-left.svg';
import '../SignUp/index.scss';
import './index.scss';
import { Loader } from '../../components/Loader/Loader';

function isRequiredFromSettings(field: any): boolean {
    return !!field.settings?.find((s: any) => s.type === 'required')?.switchValue;
}

function hasComponentScope(componentId: unknown): boolean {
    if (componentId == null) return false;
    const s = String(componentId).trim();
    return s !== '' && s !== 'undefined';
}

/** Same matching as SignUp `fieldsRender`: id + optional component_id */
function findSaveRowForApiField(saveData: QuestionAnswer[], apiField: any): QuestionAnswer | undefined {
    return saveData.find((elem) => {
        const matchById = elem.id === apiField.id;
        if (hasComponentScope(apiField.component_id)) {
            return matchById && String(elem.component_id) === String(apiField.component_id);
        }
        return matchById;
    });
}

function saveRowMatchesExpandedField(item: QuestionAnswer, apiField: any): boolean {
    if (item.id !== apiField.id) return false;
    if (hasComponentScope(apiField.component_id)) {
        return String(item.component_id) === String(apiField.component_id);
    }
    return item.component_id == null;
}

/**
 * One slider + per_service_component can become several rows (or API may already send scoped rows with component_id).
 */
function expandScorecardSectionFields(fields: any[] | undefined): any[] {
    const list = fields ?? [];
    const result: any[] = [];

    for (const field of list) {
        const perService = field.settings?.find((s: any) => s.type === 'per_service_component');
        const alreadyScoped = hasComponentScope(field.component_id);

        if (field.type === 'slider' && perService?.switchValue && !alreadyScoped) {
            const selected = (perService.options ?? []).filter((o: any) => o.selected);
            if (selected.length === 0) {
                result.push(field);
            } else {
                for (const opt of selected) {
                    result.push({
                        ...field,
                        component_id: opt.value,
                        component_name: opt.label ?? opt.name
                    });
                }
            }
        } else {
            result.push(field);
        }
    }

    return result;
}

function apiFieldToQuestionAnswer(field: any): QuestionAnswer {
    const isListType = field.type === 'multiple-choice' || field.type === 'dropdown';
    const initialAnswer = isListType
        ? Array.isArray(field.answer)
            ? field.answer
            : field.answer
            ? [field.answer]
            : []
        : field.answer ?? '';

    const desc = field.type === 'explanation-banner' ? field.description ?? field.label ?? '' : field.description;

    const required =
        field.type === 'explanation-banner' ? false : field.type === 'slider' ? isRequiredFromSettings(field) : true;

    return {
        id: field.id,
        answer: initialAnswer,
        required,
        error: false,
        component_id: field.component_id ?? null,
        type: field.type,
        name: field.label ?? field.title ?? '',
        description: desc,
        options: field.options,
        component_name: field.component_name
    };
}

/** Field types included in POST `supplier-companies/evaluation/:token` body */
const EVALUATION_PAYLOAD_FIELD_TYPES = new Set([
    'slider',
    'single-select',
    'multiple-choice',
    'dropdown',
    'short-answer',
    'paragraph'
]);

function normalizeAnswerForEvaluationPayload(
    raw: QuestionAnswer['answer'] | undefined | null,
    type: string
): string | number | boolean | string[] | null {
    if (raw === undefined || raw === null) return null;
    if (type === 'multiple-choice' || type === 'dropdown') {
        if (Array.isArray(raw)) return raw.length ? raw : null;
        const s = String(raw);
        return s ? [s] : null;
    }
    if (raw === '') return null;
    if (typeof raw === 'number' || typeof raw === 'boolean') return raw;
    if (Array.isArray(raw)) return raw.length ? raw : null;
    return String(raw);
}

function buildEvaluationSubmitState(
    sectionList: any[],
    answers: QuestionAnswer[],
    userName: string
): ScoreCardEvaluationSubmitBody {
    return {
        user_name: userName,
        sections: sectionList.map((section) => ({
            id: section.id,
            fields: expandScorecardSectionFields(section.fields)
                .filter((f: any) => EVALUATION_PAYLOAD_FIELD_TYPES.has(f.type))
                .map((f: any) => {
                    const qa = findSaveRowForApiField(answers, f);
                    const raw = qa ? qa.answer : f.answer;
                    const entry: {
                        type: string;
                        id: number;
                        answer: ReturnType<typeof normalizeAnswerForEvaluationPayload>;
                        component_id?: number | string | null;
                    } = {
                        type: f.type,
                        id: f.id,
                        answer: normalizeAnswerForEvaluationPayload(raw, f.type)
                    };
                    if (hasComponentScope(f.component_id)) {
                        entry.component_id = f.component_id;
                    }
                    return entry;
                })
        }))
    };
}

const EMPTY_SECTIONS: any[] = [];

function isScoreFieldAnswerEmpty(item: QuestionAnswer): boolean {
    if (item.type === 'slider' || item.type === 'single-select') {
        return item.answer === '' || item.answer == null;
    }
    if (Array.isArray(item.answer)) return item.answer.length === 0;
    return !item.answer;
}

/** Aligns with ScoreCard `index.scss` (desktop ≥900px uses SignUp `.area__divider`). */
const SCORE_CARD_DESKTOP_MEDIA = '(min-width: 900px)';

function useScoreCardStepperDividerClass(): 'area__divider' | 'score-card-page__section-divider' {
    const [isDesktop, setIsDesktop] = useState(
        () => typeof window !== 'undefined' && window.matchMedia(SCORE_CARD_DESKTOP_MEDIA).matches
    );

    useEffect(() => {
        const mq = window.matchMedia(SCORE_CARD_DESKTOP_MEDIA);
        const sync = () => setIsDesktop(mq.matches);
        sync();
        mq.addEventListener('change', sync);
        return () => mq.removeEventListener('change', sync);
    }, []);

    return isDesktop ? 'area__divider' : 'score-card-page__section-divider';
}

export const ScoreCard = ({
    data,
    token,
    onSubmitted,
    checkScoreCardEvaluationData
}: {
    data: any;
    token: string | null;
    onSubmitted?: () => void;
    checkScoreCardEvaluationData: ScoreCardEvaluationCheckResponse | undefined;
}) => {
    const progressData = data?.progressData;
    const userName = data?.user_name ?? '';

    const sections = useMemo(() => {
        const s = data?.formData?.sections;
        return Array.isArray(s) ? s : EMPTY_SECTIONS;
    }, [data?.formData?.sections]);

    const [activeSectionId, setActiveSectionId] = useState<string>('');
    const [saveData, setSaveData] = useState<QuestionAnswer[]>([]);

    const [completedSectionIds, setCompletedSectionIds] = useState<Set<string>>(() => new Set());

    const [submitEvaluation, { isLoading: isSubmittingScores }] = useSubmitScoreCardEvaluationMutation();
    const stepperDividerClass = useScoreCardStepperDividerClass();

    const flatFields = useMemo(
        () => sections.flatMap((s: any) => expandScorecardSectionFields(s.fields).map(apiFieldToQuestionAnswer)),
        [sections]
    );

    useEffect(() => {
        if (!flatFields.length) return;
        setSaveData(flatFields);
    }, [data?.formData?.id, data?.formData?.updated_at, flatFields]);

    useEffect(() => {
        if (!data?.formData?.id) return;
        const list = progressData?.completed_sections ?? [];
        setCompletedSectionIds(new Set(list.map((id: any) => String(id))));
    }, [data?.formData?.id, progressData?.completed_sections]);

    useEffect(() => {
        if (!sections.length) return;
        const pid = progressData?.last_section_id ?? progressData?.order_section_id;
        const resolved =
            pid != null && sections.some((s: any) => String(s.id) === String(pid))
                ? String(pid)
                : String(sections[0].id);
        setActiveSectionId(resolved);
    }, [data?.formData?.id, progressData?.last_section_id, progressData?.order_section_id, sections]);

    const resolvedActiveId = activeSectionId || (sections[0]?.id != null ? String(sections[0].id) : '');
    const activeIndex = sections.findIndex((s: any) => String(s.id) === resolvedActiveId);
    const activeSection = activeIndex >= 0 ? sections[activeIndex] : null;

    const sectionStatus = (section: any, index: number) => {
        const idStr = String(section.id);
        const isActive = idStr === resolvedActiveId;
        if (isActive) return 'active' as const;
        const isDone = index < activeIndex || completedSectionIds.has(idStr);
        if (isDone) return 'done' as const;
        return 'pending' as const;
    };

    const canOpenSection = (index: number) => index <= activeIndex;

    const updateAnswer = (id: number, value: any, type: string, component_id?: number | string | null) => {
        if (type === 'components') {
            setSaveData((prev) =>
                prev.map((item) => {
                    if (item.id === id) {
                        return {
                            ...item,
                            answer: item.answer === 'true' ? 'false' : 'true'
                        };
                    }
                    return item;
                })
            );
            return;
        }

        setSaveData((prev) =>
            prev.map((item) => {
                const isMatch = hasComponentScope(component_id)
                    ? item.id === id && String(item.component_id) === String(component_id)
                    : item.id === id && item.component_id == null;

                if (!isMatch) return item;

                switch (type) {
                    case 'short-answer':
                    case 'paragraph':
                        return { ...item, answer: value, error: false };

                    case 'multiple-choice':
                    case 'dropdown': {
                        const arr = Array.isArray(item.answer) ? item.answer : [];
                        const updated = arr.includes(value) ? arr.filter((v: any) => v !== value) : [...arr, value];
                        return { ...item, answer: updated, error: false };
                    }

                    case 'single-select':
                        return {
                            ...item,
                            answer: item.answer === value ? '' : value,
                            error: false
                        };

                    case 'slider':
                        return { ...item, answer: value, error: false };

                    default:
                        return item;
                }
            })
        );
    };

    const validateCurrentSection = (): boolean => {
        const expanded = expandScorecardSectionFields(activeSection?.fields);

        const hasError = saveData.some(
            (item) =>
                expanded.some((f) => saveRowMatchesExpandedField(item, f)) &&
                item.required &&
                isScoreFieldAnswerEmpty(item)
        );

        if (hasError) {
            setSaveData((prev) =>
                prev.map((item) => {
                    const inSection = expanded.some((f) => saveRowMatchesExpandedField(item, f));
                    if (!inSection || !item.required) return item;
                    return { ...item, error: isScoreFieldAnswerEmpty(item) };
                })
            );
            return false;
        }

        setSaveData((prev) =>
            prev.map((item) => {
                const inSection = expanded.some((f) => saveRowMatchesExpandedField(item, f));
                return inSection ? { ...item, error: false } : item;
            })
        );
        return true;
    };

    const validateAllSections = (): boolean => {
        const allExpanded = sections.flatMap((s: any) => expandScorecardSectionFields(s.fields));
        const hasError = saveData.some(
            (item) =>
                allExpanded.some((f) => saveRowMatchesExpandedField(item, f)) &&
                item.required &&
                isScoreFieldAnswerEmpty(item)
        );

        if (hasError) {
            setSaveData((prev) =>
                prev.map((item) => {
                    const inForm = allExpanded.some((f) => saveRowMatchesExpandedField(item, f));
                    if (!inForm || !item.required) return item;
                    return { ...item, error: isScoreFieldAnswerEmpty(item) };
                })
            );
            message.warning('Please complete all required fields before submitting.');
            return false;
        }

        setSaveData((prev) =>
            prev.map((item) => {
                const inForm = allExpanded.some((f) => saveRowMatchesExpandedField(item, f));
                return inForm ? { ...item, error: false } : item;
            })
        );
        return true;
    };

    const isLastSection = activeIndex >= sections.length - 1;

    async function handlePrimaryClick() {
        if (!sections.length) return;

        if (isLastSection) {
            if (!validateAllSections()) return;
            if (!token) {
                message.error('Missing evaluation link.');
                return;
            }
            const body = buildEvaluationSubmitState(sections, saveData, userName);

            try {
                await submitEvaluation({ token, body }).unwrap();
                onSubmitted?.();
            } catch {
                message.error('Could not submit scores. Please try again.');
            }
            return;
        }

        if (!validateCurrentSection()) return;

        const currentId = resolvedActiveId;
        setCompletedSectionIds((prev) => new Set([...prev, currentId]));

        const currentIndex = sections.findIndex((s: any) => String(s.id) === currentId);
        if (currentIndex < sections.length - 1) {
            setActiveSectionId(String(sections[currentIndex + 1].id));
        }
    }

    function goToPrevSection() {
        if (activeIndex <= 0) return;
        setActiveSectionId(String(sections[activeIndex - 1].id));
    }

    function goToSection(index: number) {
        if (!canOpenSection(index)) return;
        setActiveSectionId(String(sections[index].id));
    }

    if (!sections.length) {
        return (
            <div className="signup score-card-page">
                <div className="signup__main main">
                    <div className="main__questionary questionary">
                        <div className="questionary__header">
                            <div className="questionary__title">No sections in this scorecard.</div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="signup score-card-page">
            <div className="signup__main main">
                <div className="main__questionary questionary">
                    <div className="questionary__header">
                        <div className="questionary__header-block-1">
                            <div className="questionary__title">
                                Supplier Scorecard for {checkScoreCardEvaluationData?.data?.company?.name}
                            </div>
                        </div>
                    </div>

                    <div className="questionary__main">
                        <div className="questionary__EAs">
                            <div className="questionary__area area expanded">
                                <div className="area__sections expanded">
                                    {sections.map((section: any, index: number) => {
                                        const status = sectionStatus(section, index);
                                        const isActive = status === 'active';
                                        const isDone = status === 'done';
                                        const clickable = canOpenSection(index);

                                        return (
                                            <Fragment key={section.id}>
                                                <div
                                                    role={clickable ? 'button' : undefined}
                                                    tabIndex={clickable ? 0 : undefined}
                                                    aria-label={`Step ${index + 1} of ${sections.length}: ${
                                                        section.title
                                                    }`}
                                                    className={`area__section ${isActive ? 'active' : ''} ${
                                                        isDone ? 'done' : ''
                                                    } ${!isActive && !isDone ? 'pending' : ''} ${
                                                        clickable ? 'area__section--clickable' : ''
                                                    }`}
                                                    onClick={() => goToSection(index)}
                                                    onKeyDown={(e) => {
                                                        if (!clickable) return;
                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                            e.preventDefault();
                                                            goToSection(index);
                                                        }
                                                    }}
                                                >
                                                    {isDone && !isActive ? (
                                                        <div className="number_of_section">
                                                            <img src={done} alt="" />
                                                        </div>
                                                    ) : (
                                                        <div className="number_of_section">{index + 1}</div>
                                                    )}
                                                    <span className="area__section__label">{section.title}</span>
                                                </div>
                                                {index !== sections.length - 1 ? (
                                                    <div className={stepperDividerClass} />
                                                ) : null}
                                            </Fragment>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                        <div className="questionary__fields-section fields-section">
                            <div className="fields-section__components">
                                <div className="fields-section__componentsHeader componentsHeader">
                                    <div className="componentsHeader__row">
                                        <div className="componentsHeader__title">
                                            <ServiceComIcon />
                                            {activeSection?.title}
                                        </div>
                                        <div className="componentsHeader__section-number">
                                            Section {activeIndex + 1} of {sections.length}
                                        </div>
                                    </div>
                                    {activeSection?.description ? (
                                        <div className="componentsHeader__row desc">{activeSection.description}</div>
                                    ) : null}
                                </div>
                                <div>
                                    {expandScorecardSectionFields(activeSection?.fields).map((apiField: any) => {
                                        const field = findSaveRowForApiField(saveData, apiField);
                                        if (!field) return null;

                                        const rowKey = `${field.id}-${field.component_id ?? ''}`;

                                        if (field.type === 'single-select') {
                                            return (
                                                <SingleSelectScorecard
                                                    key={rowKey}
                                                    field={field}
                                                    required={field.required}
                                                    updateAnswer={updateAnswer}
                                                    saveData={saveData}
                                                    component={field?.component_name}
                                                    component_id={field.component_id}
                                                />
                                            );
                                        }

                                        if (field.type === 'explanation-banner') {
                                            return (
                                                <ExplanationBanner
                                                    key={rowKey}
                                                    field={field}
                                                    required={field.required}
                                                    saveData={saveData}
                                                    component_id={null}
                                                />
                                            );
                                        }

                                        if (field.type === 'slider') {
                                            const rawAnswer = field.answer;
                                            const sliderValue =
                                                rawAnswer == null || Array.isArray(rawAnswer)
                                                    ? undefined
                                                    : typeof rawAnswer === 'string' || typeof rawAnswer === 'number'
                                                    ? rawAnswer
                                                    : String(rawAnswer);
                                            const sliderOptions = (field.options ?? []).map((o: any) => ({
                                                label: o.label,
                                                value: o.value
                                            }));
                                            const sliderComponentId = hasComponentScope(field.component_id)
                                                ? field.component_id
                                                : undefined;
                                            return (
                                                <div className="Field score-card-field" key={rowKey}>
                                                    {field.component_name ? (
                                                        <div className="Field__component">{field.component_name}</div>
                                                    ) : null}
                                                    <ScoreCardSliderField
                                                        title={field.name}
                                                        description={field.description}
                                                        options={sliderOptions}
                                                        value={sliderValue}
                                                        required={field.required}
                                                        error={!!field.error}
                                                        onSelect={(v) =>
                                                            updateAnswer(field.id, v, 'slider', sliderComponentId)
                                                        }
                                                    />
                                                </div>
                                            );
                                        }

                                        return null;
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="questionary__btns">
                        <Button onClick={() => goToPrevSection()} disabled={activeIndex <= 0}>
                            <div className="questionary__btn">
                                <img src={arrowLeftIcon} alt="" />
                                Previous
                            </div>
                        </Button>
                        <Button
                            type="primary"
                            onClick={() => {
                                if (isSubmittingScores) return;
                                handlePrimaryClick();
                            }}
                        >
                            <div className="questionary__btn">
                                {isSubmittingScores ? <Loader /> : isLastSection ? 'Submit Scores' : 'Next Step'}{' '}
                                <img src={arrowIcon} alt="" />
                            </div>
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};
